import { DishRowData } from "./costPriceDataTypes";

/**
 * Export helpers for the cost price table (CSV file + clipboard).
 *
 * The rows handed in are exactly the rows the table is showing (all filters,
 * search and sorting already applied) — the export never re-derives or
 * re-filters data, so what you see is what you get.
 *
 * Cells that render as "-" / "--" in the table (placeholders without a cost,
 * CHOICE rows that carry no menu price / matrix / popularity) are exported as
 * empty cells so spreadsheets keep the columns numeric.
 */

/**
 * The nine dish-level cells, in table order. Header labels are passed in
 * translated by the caller. Shared with the detailed export
 * (`costPriceDetailedExport.ts`), which re-positions these exact values into its
 * wider column set — so a dish row reads identically in both files.
 */
export const buildDishCells = (row: DishRowData): (string | number)[] => {
    const isPlaceholder = row.isPlaceholder || row.totalCost === 0;
    const hasPrice = !row.isChoiceCost && row.price !== 0;

    return [
        // Display name, so a partner-venue dish exports as the table shows it; the next
        // cell still carries the raw revenue-item name it links on.
        row.displayName || row.dishName,
        row.menuItemName || '',
        row.category || '',
        row.isChoiceCost || !row.matrix ? '' : row.matrix,
        row.isChoiceCost && row.totalCost === 0 ? '' : isPlaceholder ? '' : row.totalCost.toFixed(2),
        hasPrice ? row.price.toFixed(2) : '',
        hasPrice && !isPlaceholder ? `${row.costPercentage.toFixed(1)}%` : '',
        hasPrice && !isPlaceholder ? row.margin.toFixed(2) : '',
        row.isChoiceCost ? '' : row.popularity,
    ];
};

export const escapeCsvCell = (value: string | number): string => {
    const s = String(value ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Tabs/newlines inside a cell would break the column alignment when pasted. */
const escapeTsvCell = (value: string | number): string =>
    String(value ?? '').replace(/[\t\r\n]+/g, ' ');

export function buildCostPriceCsv(rows: DishRowData[], headers: string[]): string {
    const lines = [headers.map(escapeCsvCell).join(',')];
    for (const row of rows) {
        lines.push(buildDishCells(row).map(escapeCsvCell).join(','));
    }
    return lines.join('\n');
}

/** Tab-separated so it pastes straight into Excel / Google Sheets as a table. */
export function buildCostPriceClipboardText(rows: DishRowData[], headers: string[]): string {
    const lines = [headers.map(escapeTsvCell).join('\t')];
    for (const row of rows) {
        lines.push(buildDishCells(row).map(escapeTsvCell).join('\t'));
    }
    return lines.join('\n');
}
